import React, { useState, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import dayjs from 'dayjs';
import { useHistory } from 'react-router-dom';
import customParseFormat from 'dayjs/plugin/customParseFormat';

import { createLead, leadComment } from 'api/leads';
import InputField from 'components/FormFields/InputField';
import FormSelect from 'components/FormFields/FormSelect';
import FormTextarea from 'components/FormFields/FormTextarea';
import APFormGroup from 'components/FormFields/APFormGroup';
import CountryWidget from 'components/FormFields/CountryWidget';
import TimeZoneSelect from 'components/FormFields/TimeZoneSelect';
import TimeSettingFormItem from 'components/FormFields/TimeSettingFormItem';
import CalendarWidget from 'components/FormFields/CalendarWidget';
import LocationSelect from 'components/LocationSelect';
import FormCheckbox from 'components/FormFields/FormCheckbox';
import CurrencyInput from 'components/FormFields/CurrencyInput';
import APModal from 'components/Modal/APModal';
import { timeFormatValidation, getTimeList } from 'utils/time';
import { prepareLocationForApi } from 'utils/location';
import { isEmailValid, isPhoneValid, objToCamelCase, objToSnake } from 'utils/general';
import { pushSiteError } from 'containers/App/actions';

dayjs.extend(customParseFormat);

const today = dayjs().format('YYYY-MM-DD');

const DEFAULT_FORM_STATE = {
  projectType: 'wedding',
  serviceType: null,
  projectName: '',
  eventDate: today,
  allDay: true,
  startTime: null,
  endTime: null,
  venueName: '',
  location: {},
  estimatedBudget: 0,
  estimatedGuestCount: 0,
  inquiryDate: today,
  leadSource: null,
  notes: '',
  role: '',
  contactFirstName: '',
  contactLastName: '',
  contactCompanyName: '',
  contact: {},
  party: [['bride', '', ''], ['groom', '', '']],
  timezone: '',
};

const isUrlValid = (website = '') => {
  const websiteUrl = website.trim();
  if (!websiteUrl) return true;

  const urlPatternString = [
    '^(?:http)s?://',
    '(?:(?:[A-Z0-9](?:[A-Z0-9-]{0,61}[A-Z0-9])?\\.)+',
    '(?:[A-Z]{2,6}\\.?|[A-Z0-9-]{2,}\\.?)|',
    'localhost|',
    '\\d{1,3}(?:\\.\\d{1,3}){3})',
    '(?::\\d+)?',
    '(?:/?|[/?]\\S+)$',
  ].join('');

  // Create the regex pattern using the RegExp constructor
  const urlPattern = new RegExp(urlPatternString, 'i');
  return !!urlPattern.test(websiteUrl);
};

export const CreateLeadModal = ({
  isOpen,
  dismiss,
}) => {
  const organization = useSelector((state) => state.global.userInfo.organization);
  const preferences = useSelector((state) => state.leads.preferences);
  const [formState, setFormState] = useState(DEFAULT_FORM_STATE);
  const [error, setError] = useState('');
  const [isFormValid, setFormValid] = useState(true);
  const [isWebsiteUrlValid, setWebsiteUrlValid] = useState(true);
  const [isEmailAddressValid, setEmailAddressValid] = useState(true);
  const [isPhoneNumberValid, setPhoneNumberValid] = useState(true);

  const dispatch = useDispatch();
  const history = useHistory();

  const {
    id: orgId,
    datePreference = 'MM/DD/YYYY',
    timePreference = 12,
    currency,
  } = organization || {};

  // Leads custom preferences
  const serviceTypes = useMemo(() => {
    return preferences.filter(pref => (
      pref.kind === 'service-type' && pref.visibility
    ));
  }, [preferences]);
  const leadSources = useMemo(() => {
    return preferences.filter(pref => (
      pref.kind === 'lead-source' && pref.visibility
    ));
  }, [preferences]);

  const handleFormChange = useCallback((updater) => {
    setFormState(prev => {
      const update = typeof updater === 'function' ? updater(prev) : updater;
      return {
        ...prev,
        ...update,
      };
    });
  }, []);

  const createNewLead = async (payload) => {
    try {
      const response = await createLead(orgId, payload);
      if (response.ok) {
        const { data } = response;
        await leadComment(orgId, data.id);
        history.push(`/app/v2/${orgId}/leads/lead/${data.id}`);
      } else {
        dispatch(pushSiteError({ message: "Your lead couldn't be created. Please try again." }));
      }
    } catch {
      dispatch(pushSiteError({ message: "Your lead couldn't be created. Please try again." }));
    }
  };

  const validateForm = () => {
    if (!(formState.contactFirstName && formState.contactLastName && formState.contact.email)) {
      setError('Please fill out all the required fields.');
      setFormValid(false);
      return false;
    }

    if (formState.contact.website && !isUrlValid(formState.contact.website)) {
      setError('Please review and update your website format to continue.');
      setFormValid(false);
      setWebsiteUrlValid(false);
      return false;
    }

    if (formState.contact.email && !isEmailValid(formState.contact.email)) {
      setError('Please review and update your email format to continue.');
      setFormValid(false);
      setEmailAddressValid(false);
      return false;
    }

    if (formState.contact.phone && !isPhoneValid(formState.contact.phone)) {
      setError('Please review and provide a valid phone number.');
      setFormValid(false);
      setPhoneNumberValid(false);
      return false;
    }

    setError('');
    setFormValid(true);
    setWebsiteUrlValid(true);
    setEmailAddressValid(true);
    setPhoneNumberValid(true);

    return true;
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      return;
    }
    // Need to transform the data to be accepted by the api.
    const {
      contact,
      eventDate,
      serviceType,
      leadSource,
      inquiryDate,
      ...payload
    } = formState || {};
    const {
      email,
      phone,
      website,
      street,
      extended,
      city,
      region,
      postalCode,
      country,
    } = contact || {};
    const _contact = {
      phones: [
        {
          field_value: phone || '',
        },
      ],
      emails: [
        {
          field_value: email || '',
        },
      ],
      websites: [
        {
          field_value: website || '',
        },
      ],
      addresses: [
        {
          _isCreating: true,
          street,
          extended,
          city,
          region,
          postcode: postalCode,
          country,
        },
      ],
    };

    createNewLead({
      ...objToSnake(payload),
      contact: _contact,
      wedding_date: eventDate,
      lead_source: leadSource,
      lead_source_id: leadSource,
      service_type: serviceType,
      service_type_id: serviceType,
      date_inquiry: inquiryDate,
      // Extraneous data needed for the request to succeed.
      address: { city: '', region: '' },
      organization_id: orgId,
    });
  };

  return (
    <APModal
      isOpen={isOpen}
      title="Create a new lead."
      className="settings_project_modal__container"
      errorMsg={error}
      actions={(
        <button
          className="button"
          onClick={handleSubmit}
        >
          Create Lead
        </button>
      )}
      onClose={dismiss}
    >
      <CreateLeadForm
        formState={formState}
        onFormChange={handleFormChange}
        serviceTypes={serviceTypes}
        leadSources={leadSources}
        datePreference={datePreference}
        timePreference={timePreference}
        currency={currency}
        isFormValid={isFormValid}
        isWebsiteUrlValid={isWebsiteUrlValid}
        setWebsiteUrlValid={setWebsiteUrlValid}
        isEmailAddressValid={isEmailAddressValid}
        isPhoneNumberValid={isPhoneNumberValid}
        setPhoneNumberValid={setPhoneNumberValid}
      />
    </APModal>
  );
};

const CreateLeadForm = ({
  onFormChange,
  formState,
  serviceTypes,
  leadSources,
  datePreference,
  timePreference,
  currency,
  isFormValid,
  isWebsiteUrlValid,
  setWebsiteUrlValid,
  isEmailAddressValid,
  isPhoneNumberValid,
  setPhoneNumberValid,
}) => {
  const {
    projectType,
    serviceType,
    projectName,
    timezone,
    eventDate,
    allDay,
    startTime,
    endTime,
    venueName,
    location: rawLocation,
    estimatedBudget,
    estimatedGuestCount,
    inquiryDate,
    leadSource,
    notes,
    contact: primaryContact,
    party,
    role,
    contactFirstName,
    contactLastName,
    contactCompanyName,
  } = formState;

  const {
    email,
    phone,
    website,
    street,
    extended,
    city,
    region,
    postalCode,
    country,
  } = primaryContact || {};

  // Date picker displays date according to org settings.
  const displayEventDate = useMemo(() => (
    dayjs(eventDate, 'YYYY-MM-DD').format(datePreference.toUpperCase())
  ), [eventDate, datePreference]);
  // react-datepicker expects MM/DD/YYYY format by default.
  const pickerEventDate = useMemo(() => dayjs(eventDate, 'YYYY-MM-DD').format('MM/DD/YYYY'), [eventDate]);
  const displayInquiryDate = useMemo(() => (
    dayjs(inquiryDate, 'YYYY-MM-DD').format(datePreference.toUpperCase())
  ), [inquiryDate, datePreference]);
  const pickerInquiryDate = useMemo(() => dayjs(inquiryDate, 'YYYY-MM-DD').format('MM/DD/YYYY'), [inquiryDate]);

  const timeList = useMemo(() => getTimeList(timePreference), [timePreference]);

  const [primaryParty, secondaryParty] = party || [];
  const [primaryRole] = primaryParty || [];
  const [secondaryRole, secondaryFirstName, secondaryLastName] = secondaryParty || [];

  const location = useMemo(() => objToCamelCase(rawLocation), [rawLocation]);

  const getRoleOptions = useCallback(() => {
    const roleList = ['bride', 'groom', 'guest-of-honor', 'partner', 'bridegroom'];
    const transform = (str) => str
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
    return roleList.map((_role) => {
      return (
        <option value={_role}>{transform(_role)}</option>
      );
    });
  }, []);

  const handleLocationSelect = (value) => {
    const transformed = prepareLocationForApi(value);
    onFormChange({ location: transformed });
  };

  const handlePartyChange = (rank, field, value) => {
    const formChanges = {};
    let index = 0;
    let subindex = 0;
    switch(rank) {
      case 'primary':
        if (field === 'firstName') {
          formChanges.contactFirstName = value;
          subindex = 1;
        } else if (field === 'lastName') {
          formChanges.contactLastName = value;
          subindex = 2;
        }
        break;
      case 'secondary':
        index = 1;
        if (field === 'firstName') {
          subindex = 1;
        } else if (field === 'lastName') {
          subindex = 2;
        }
        break;
      default:
        return;
    }
    const updatedParty = [...party];
    updatedParty[index][subindex] = value;
    formChanges.party = updatedParty;
    onFormChange(formChanges);
  };

  /**
   * Handles all day checkbox change since it has additional logic with start/end time.
   * @param {boolean} value
   */
  const handleAllDayChange = (value) => {
    const formChanges = { allDay: value };
    if (value) {
      // No start/end time if event is all day.
      formChanges.startTime = null;
      formChanges.endTime = null;
    } else {
      // Default start/end time.
      formChanges.startTime = '09:00:00';
      formChanges.endTime = '10:00:00';
    }
    onFormChange(formChanges);
  };

  const handleDateChange = (field, value) => {
    const normalizedValue = dayjs(value, 'MM/DD/YYYY').format('YYYY-MM-DD');
    onFormChange({ [field]: normalizedValue });
  };

  // Need to make sure start time isn't later than end time so we want to handle
  // time updates for both start and end in one go, then update a separate state variable
  // that will fire the update function.
  const handleTimeChange = (field, value) => {
    const data = {};
    const time = timeFormatValidation(value);

    if (!time) return;

    const start = dayjs(startTime, 'HH:mm:ss');
    const end = dayjs(endTime, 'HH:mm:ss');
    const newTime = dayjs(time, 'HH:mm:ss');
    switch (field) {
      case 'startTime':
        data.startTime = newTime.format('HH:mm:ss');
        if (!endTime || newTime.isSameOrAfter(end)) {
          data.endTime = newTime.add(1, 'hour').format('HH:mm:ss');
        }
        break;
      case 'endTime':
        data.endTime = newTime.format('HH:mm:ss');
        if (!startTime || start.isSameOrAfter(end)) {
          data.startTime = newTime.subtract(1, 'hour').format('HH:mm:ss');
        }
        break;
      default:
        return;
    }
    onFormChange(data);
  };

  const handleAddressChange = (data) => {
    onFormChange((prev) => ({ contact: { ...prev.contact, ...data } }));
  };

  const handleContactChange = (data) => {
    onFormChange((prev) => ({ contact: { ...prev.contact, ...data } }));
  };

  return (
    <div className="create-lead-form">
      <APFormGroup
        title="Project Details"
        verticalGap={0}
      >
        <FormSelect
          id="project-type"
          name="Project Type"
          onChange={(e) => onFormChange({ projectType: e.target.value })}
          value={projectType}
        >
          <option
            value="wedding"
          >
            Wedding
          </option>
          <option
            value="other"
          >
            Event or Project
          </option>
        </FormSelect>
        <FormSelect
          id="service-type"
          name="Service Type"
          onChange={(e) => onFormChange({ serviceType: parseInt(e.target.value, 10) })}
          value={serviceType}
          emptyValue="Select Service Type..."
        >
          {serviceTypes.map((type) => {
            return (
              <option
                key={`service-type-${type.id}`}
                value={type.id}
              >
                {type.name}
              </option>
            );
          })}
        </FormSelect>
      </APFormGroup>
      <APFormGroup
        verticalGap={0}
      >
        <InputField
          name="project-name"
          label="Project name"
          onChange={(e) => onFormChange({ projectName: e.target.value })}
          value={projectName}
        />
        <TimeZoneSelect
          labelHidden={false}
          name="Time Zone"
          onChange={(e) => onFormChange({ timezone: e.target.value })}
          timezone={timezone}
        />
      </APFormGroup>
      <APFormGroup
        title="Primary Contact Information"
        verticalGap={0}
      >
        <InputField
          name="contact-first-name"
          label="First Name"
          onChange={(e) => handlePartyChange('primary', 'firstName', e.target.value)}
          value={contactFirstName}
          required
          showError={!isFormValid && !contactFirstName}
          autoFocus
        />
        <InputField
          name="contact-last-name"
          label="Last Name"
          onChange={(e) => handlePartyChange('primary', 'lastName', e.target.value)}
          value={contactLastName}
          required
          showError={!isFormValid && !contactLastName}
        />
      </APFormGroup>
      {projectType === 'wedding' && (
        <APFormGroup
          verticalGap={0}
        >
          <FormSelect
            id="primary-role"
            name="Role"
            value={primaryRole}
            onChange={(e) => handlePartyChange('primary', 'role', e.target.value)}
          >
            {getRoleOptions()}
          </FormSelect>
        </APFormGroup>
      )}
      <APFormGroup
        verticalGap={0}
      >
        <InputField
          name="contact-company-name"
          label="Company Name"
          onChange={(e) => onFormChange({ contactCompanyName: e.target.value })}
          value={contactCompanyName}
        />
        <InputField
          name="contact-title"
          label="Title"
          value={role}
          onChange={(e) => onFormChange({ role: e.target.value })}
        />
      </APFormGroup>
      <APFormGroup
        verticalGap={0}
      >
        <InputField
          name="contact-email"
          label="Email"
          onChange={(e) => handleContactChange({ email: e.target.value })}
          value={email}
          required
          showError={!isFormValid && !email || !isEmailAddressValid}
        />
        <InputField
          name="contact-phone"
          label="Phone"
          onChange={(e) => {
            setPhoneNumberValid(true);
            handleContactChange({ phone: e.target.value });
          }}
          value={phone}
          onBlur={(e) => {
            const phoneNumber = (e.target.value || '');
            setPhoneNumberValid(!phoneNumber || isPhoneValid(phoneNumber));
          }}
          showError={!isPhoneNumberValid}
        />
      </APFormGroup>
      <APFormGroup
        verticalGap={0}
      >
        <InputField
          name="contact-website"
          label="Website"
          onChange={(e) => {
            setWebsiteUrlValid(true);
            handleContactChange({ website: e.target.value });
          }}
          onBlur={(e) => {
            const _website = (e.target.value || '');
            setWebsiteUrlValid(!_website || isUrlValid(_website));
          }}
          value={website}
          showError={!isWebsiteUrlValid}
        />
      </APFormGroup>
      <APFormGroup
        verticalGap={0}
      >
        <InputField
          name="contact-address-line-1"
          label="Address"
          onChange={(e) => handleAddressChange({ street: e.target.value })}
          value={street}
        />
      </APFormGroup>
      <APFormGroup
        verticalGap={0}
      >
        <InputField
          name="contact-address-line-2"
          label="Address Line 2"
          onChange={(e) => handleAddressChange({ extended: e.target.value })}
          value={extended}
        />
      </APFormGroup>
      <APFormGroup verticalGap={0}>
        <InputField
          name="contact-address-city"
          label="City"
          onChange={(e) => handleAddressChange({ city: e.target.value })}
          value={city}
        />
        <InputField
          name="contact-address-state"
          label="State/Province"
          onChange={(e) => handleAddressChange({ region: e.target.value })}
          value={region}
        />
        <InputField
          name="contact-address-postal-code"
          label="Postal Code"
          onChange={(e) => handleAddressChange({ postalCode: e.target.value })}
          value={postalCode}
        />
      </APFormGroup>
      <APFormGroup>
        <CountryWidget
          name="Country"
          id="country-widget"
          onChange={({ label }) => handleAddressChange({ country: label })}
          value={{ label: country || '' }}
        />
      </APFormGroup>
      {projectType === 'wedding' && (
        <>
          <APFormGroup
            title="Other Party Member"
            verticalGap={0}
          >
            <InputField
              name="secondary-contact-first-name"
              label="First name"
              value={secondaryFirstName}
              onChange={(e) => handlePartyChange('secondary', 'firstName', e.target.value)}
            />
            <InputField
              name="secondary-contact-last-name"
              label="Last name"
              value={secondaryLastName}
              onChange={(e) => handlePartyChange('secondary', 'lastName', e.target.value)}
            />
          </APFormGroup>
          <APFormGroup>
            <FormSelect
              id="secondary-role"
              name="Role"
              value={secondaryRole}
              onChange={(e) => handlePartyChange('secondary', 'role', e.target.value)}
            >
              {getRoleOptions()}
            </FormSelect>
          </APFormGroup>
        </>
      )}
      <APFormGroup
        title="Additional Project Details"
        verticalGap={0}
      >
        <CalendarWidget
          id="event-date"
          name="Event Date"
          setDate={(value) => handleDateChange('eventDate', value)}
          nNextYears={10}
          showNextYears
          value={displayEventDate}
          pickerDate={pickerEventDate}
        />
        <APFormGroup
          verticalGap={0}
          horizontalGap={2}
        >
          {!allDay && (
            <>
              <TimeSettingFormItem
                name="Start time"
                setTime={(value) => handleTimeChange('startTime', value)}
                time={startTime || '9:00:00'}
                timeFormat={timePreference}
                timeList={timeList}
                value={startTime}
              />
              <TimeSettingFormItem
                name="End time"
                setTime={(value) => handleTimeChange('endTime', value)}
                time={endTime || '10:00:00'}
                timeList={timeList}
                timeFormat={timePreference}
                value={endTime}
              />
            </>
          )}
        </APFormGroup>
      </APFormGroup>
      <APFormGroup
        verticalGap={0}
      >
        <FormCheckbox
          checked={allDay}
          name="All day"
          onChange={(e) => handleAllDayChange(e.target.checked)}
          wrapperProps={{
            className: 'small-checkbox',
          }}
          labelProps={{
            className: 'form-item__label--unstyled',
          }}
        >
          All day event
        </FormCheckbox>
      </APFormGroup>
      <APFormGroup
        verticalGap={0}
        horizontalGap={2}
      >
        <InputField
          name="venue"
          label="Venue"
          onChange={(e) => onFormChange({ venueName: e.target.value })}
          value={venueName}
        />
        <LocationSelect
          name="location"
          label="Location"
          setLocation={(value) => handleLocationSelect(value)}
          location={location}
        />
      </APFormGroup>
      <APFormGroup
        verticalGap={0}
        horizontalGap={2}
      >
        <CurrencyInput
          name="budget"
          label="Budget"
          cents={estimatedBudget}
          currency={currency}
          onChange={(value) => onFormChange({ estimatedBudget: value })}
        />
        <InputField
          name="guest-count"
          label="Guest Count"
          type="number"
          onChange={(e) => onFormChange({ estimatedGuestCount: e.target.value })}
          value={estimatedGuestCount > 0 ? estimatedGuestCount : ''}
        />
      </APFormGroup>
      <APFormGroup
        verticalGap={0}
        horizontalGap={2}
      >
        <CalendarWidget
          id="inquiry-date"
          name="Inquiry Date"
          setDate={(value) => handleDateChange('inquiryDate', value)}
          nNextYears={10}
          nPastYears={3}
          value={displayInquiryDate}
          pickerDate={pickerInquiryDate}
        />
        <FormSelect
          id="lead-source"
          name="Lead Source"
          value={leadSource}
          onChange={(e) => onFormChange({ leadSource: parseInt(e.target.value, 10) })}
        >
          {leadSources.map(source => {
            return (
              <option
                key={`lead-source-${source.id}`}
                value={source.id}
              >
                {source.name}
              </option>
            );
          })}
        </FormSelect>
      </APFormGroup>
      <APFormGroup>
        <FormTextarea
          name="notes"
          label="Notes"
          onChange={(e) => onFormChange({ notes: e.target.value })}
          value={notes}
        />
      </APFormGroup>
    </div>
  );
};

export default CreateLeadForm;
